import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import Spinner from "../../../components/common/Spinner";
import useAuth from "../../../Hooks/useAuth";
import DashboardMainContainer from "../../../components/common/Containers/DashboardMainContainer";
import Title from "../../../components/common/Title/Title";
import PortfolioEditPopup from "../../../components/BusinessDashboard/PortfolioEditPopup";
import photos from "../../../sections/Portfolio/photos";
import { AdminMainText } from "../../../sections/AdminDashboard/shared/adminUx";
import PhotoAlbum from "react-photo-album";
import { validateFile, getAcceptAttribute } from "../../../utils/contentSafety";
import Alerts from "../../../components/common/Alerts/Alerts";
const Portfolio = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [ImageList, setImageList] = useState([]);
  const [RequestStart, setRequetStart] = useState({
    isProcessing: false,
    isRequestSuccess: false,
    isRequestError: false,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadedImages, setUploadedImages] = useState({});
  
  // Check user plan type
  const isProPlan = auth?.userPlan?.plan?.name?.toLowerCase() === 'pro';
  
  // Set max images based on plan: 8 for Basic, 12 for Pro
  const maxImages = isProPlan ? 12 : 8;
  
  // Popup state management
  const [popupState, setPopupState] = useState({
    isOpen: false,
    editingItem: null,
    editingType: null // 'main', 'overlay-1', 'overlay-2', 'overlay-3', or 'portfolio-{index}'
  });
  
  // Portfolio items with titles
  const [portfolioItems, setPortfolioItems] = useState({});

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get photos limited by plan and add index to each photo
  const displayPhotos = photos.slice(0, maxImages).map((photo, index) => ({
    ...photo,
    index
  }));

  // Handle opening edit popup
  const handleEditClick = (itemType, itemData = null) => {
    // Create a hidden file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = getAcceptAttribute('image');
    fileInput.style.display = 'none';
    
    // Handle file selection
    fileInput.onchange = (event) => {
      const file = event.target.files[0];
      event.target.value = '';
      if (!file) {
        document.body.removeChild(fileInput);
        return;
      }
      const result = validateFile(file, "image");
      if (!result.valid) {
        Alerts.error(result.message);
        document.body.removeChild(fileInput);
        return;
      }
      // Create preview URL
      const imagePreview = URL.createObjectURL(file);
      // Open popup with selected image
      setPopupState({
        isOpen: true,
        editingItem: {
          ...itemData,
          image: file,
          imagePreview: imagePreview
        },
        editingType: itemType
      });
      document.body.removeChild(fileInput);
    };
    
    // Add to DOM and trigger click
    document.body.appendChild(fileInput);
    fileInput.click();
  };

  // Handle saving portfolio item
  const handleSavePortfolioItem = (formData) => {
    const { editingType } = popupState;
    
    if (editingType.startsWith('portfolio-')) {
      const index = editingType.split('-')[1];
      setPortfolioItems(prev => ({
        ...prev,
        [index]: {
          image: formData.image,
          title: formData.title,
          imagePreview: formData.imagePreview
        }
      }));
    }
  };


  // Handle closing popup
  const handleClosePopup = () => {
    setPopupState({
      isOpen: false,
      editingItem: null,
      editingType: null
    });
  };


  return (
    <>
      <ToastContainer autoClose={3000} />
      <DashboardMainContainer className="!tp-container !bg-white !p-0">
        <PhotoAlbum
               layout="masonry"
               photos={displayPhotos}
               spacing={32}
               columns={(containerWidth) => {
                 if (containerWidth < 640) return 1;
                 if (containerWidth < 1024) return 2;
                 return 3;
               }}
               renderPhoto={({ photo, imageProps, wrapperStyle }) => (
                 <div style={wrapperStyle} className="group relative bg-mistygray border border-mistygray rounded-xl">
                   <img
                     {...imageProps}
                     src={portfolioItems[photo.index]?.imagePreview || ""}
                     alt={portfolioItems[photo.index]?.title || `Portfolio ${photo.index + 1}`}
                     className={`w-full h-auto object-cover rounded-xl ${portfolioItems[photo.index]?.imagePreview ? 'opacity-100' : 'opacity-0'}`}
                     style={{
                       ...imageProps.style,
                       borderRadius: "12px",
                       objectFit: "cover",
                       marginBottom: "0px",
                     }}
                   />
                   {/* Gradient overlay */}
                   {portfolioItems[photo.index]?.title && (
                     <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-50 transition-opacity duration-300 rounded-xl z-[2]"></div>
                   )}
                   {/* Title text */}
                   {portfolioItems[photo.index]?.title && (
                     <div className="absolute bottom-0 left-0 right-0 p-4 z-[3] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                       <AdminMainText className="w-full overflow-hidden text-white text-ellipsis break-words block">
                         {portfolioItems[photo.index].title}
                       </AdminMainText>
                     </div>
                   )}
                   {/* Edit overlay */}
                   <div className="absolute inset-0 bg-black opacity-0 hover:opacity-35 transition-opacity duration-300 rounded-xl"></div>
                   <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-100 z-20">
                     <button
                       onClick={() => handleEditClick(`portfolio-${photo.index}`, portfolioItems[photo.index])}
                       className="flex items-center justify-center bg-white size-[48px] rounded-full cursor-pointer transition-colors duration-200"
                       onMouseEnter={(e) => {
                         const overlay = e.currentTarget.parentElement.parentElement.querySelector('.absolute.inset-0.bg-black');
                         if (overlay) overlay.classList.add('opacity-35');
                       }}
                       onMouseLeave={(e) => {
                         const overlay = e.currentTarget.parentElement.parentElement.querySelector('.absolute.inset-0.bg-black');
                         if (overlay) overlay.classList.remove('opacity-35');
                       }}
                     >
                       <Title text={"Edit"} width={"40"}>
                         <i className="fi fi-rr-pencil text-base text-offblack hover:text-mediumgray flex items-center justify-center"></i>
                       </Title>
                     </button>
                   </div>
                 </div>
               )}
             />
      </DashboardMainContainer>
      
      {/* Portfolio Edit Popup */}
      <PortfolioEditPopup
        isOpen={popupState.isOpen}
        onClose={handleClosePopup}
        onSave={handleSavePortfolioItem}
        initialData={popupState.editingItem}
      />
    </>
  );
};

export default Portfolio;
